import { Box, Typography } from "@mui/material";
import { hasViewAccessToResource } from "api/general";
import { useSegments } from "api/segments";
import { useAppSelector } from "app/hooks";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { ReactComponent as RefreshCcw04Icon } from "components/Icons/refresh-ccw-04.svg";
import { ImportCsvModal } from "components/Layout/ImportCsv/ImportCsvModal/ImportCsvModal";
import { ImportStartedModal } from "components/Layout/ImportCsv/ImportStartedModal/ImportStartedModal";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { LinkItem } from "LinkItem";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { MEMBERS_IN_SEGMENT_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { SegmentMemberItem } from "types/segments";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { capitalizeFirstLetter } from "utilities/strings";
import { EmptySegment } from "./EmptySegment";
import { useStyles } from "./SingleSegment.styles";

export enum Modals {
  ImportCsv,
  ImportStarted,
  ConfirmImport,
}

function createItems(items: SegmentMemberItem[]): Row[] {
  return items.map((item) => ({
    cells: [
      {
        content: (
          <LinkItem to={links.singleMemberGeneralInfo(item.customerId)}>
            {item.firstName}
          </LinkItem>
        ),
        property: "firstName",
        dataHjSuppress: true,
      },
      { content: item.lastName, property: "lastName", dataHjSuppress: true },
      { content: item.phone, property: "phone", dataHjSuppress: true },
      { content: item.email, property: "email", dataHjSuppress: true },
      {
        content: capitalizeFirstLetter(item.gender),
        property: "gender",
        dataHjSuppress: true,
      },
      {
        content: OLDate(item.birthDate, { formatString: "yyyy-MM-dd" }),
        property: "birthDate",
        dataHjSuppress: true,
      },
      { content: OLDate(item.createdAt), property: "createdAt" },
    ],
    id: item.customerId,
    testId: "member-in-segment-list-row",
  }));
}

export function MembersInSegmentList({
  isStaticSegment,
  lastRecalculation,
}: {
  isStaticSegment: boolean;
  lastRecalculation?: string;
}) {
  const { message } = useMessage();
  const classes = useStyles();

  const navigate = useNavigate();
  const { listing, updateListing } = useListing(MEMBERS_IN_SEGMENT_LIST_ID);
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const { id: segmentId } = useParams<{ id: string }>();
  const { useMembersInSegmentList, importSegmentMembers } = useSegments({
    segmentId,
  });
  const { notify } = useNotifications();

  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[MEMBERS_IN_SEGMENT_LIST_ID]?.filters || [],
  );

  const [selectedModal, setSelectedModal] = useState<{
    mode: Modals | null;
    isOpen: boolean;
  }>({ mode: null, isOpen: false });

  const setSelectedFilters = (
    filters: React.SetStateAction<SelectedFilterV2[]>,
  ) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      MEMBERS_IN_SEGMENT_LIST_ID,
    );

    setFilters(filters);
  };

  const { data: membersInSegment, error } = useMembersInSegmentList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "firstName" }),
      property: "firstName",
      isSortable: true,
    },
    {
      headerText: message({ id: "lastName" }),
      property: "lastName",
      isSortable: true,
    },
    {
      headerText: message({ id: "phoneNumber" }),
      property: "phone",
      isSortable: true,
    },
    {
      headerText: message({ id: "email" }),
      property: "email",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "gender" }),
      property: "gender",
      isSortable: true,
    },
    {
      headerText: message({ id: "dateOfBirth" }),
      property: "birthDate",
      isSortable: true,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      isSortable: true,
    },
  ]);

  const items = membersInSegment?.items
    ? createItems(membersInSegment.items)
    : [];

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && membersInSegment) {
      setPersistentFilters(filters, MEMBERS_IN_SEGMENT_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, membersInSegment, error]);
  const hasViewImportPermission = hasViewAccessToResource("IMPORT");

  const segmentIsEmpty = membersInSegment?.items.length === 0;

  return (
    <>
      {isStaticSegment && segmentIsEmpty ? (
        <EmptySegment
          openImportCsvModal={() => {
            setSelectedModal({ mode: Modals.ImportCsv, isOpen: true });
          }}
        />
      ) : (
        <>
          <Subheader>
            <FiltersV2
              data-test-id="member-in-segment"
              donutLabel={message({ id: "members" })}
              savedFilters={getNewFiltersOnly(filters)}
              properties={[
                {
                  query: "customerId",
                  label: message({ id: "memberID" }),
                  type: "text",
                },
                {
                  query: "firstName",
                  label: message({ id: "firstName" }),
                  type: "text",
                },
                {
                  query: "lastName",
                  label: message({ id: "lastName" }),
                  type: "text",
                },
                {
                  query: "phone",
                  label: message({ id: "phoneNumber" }),
                  type: "member-phone-filter",
                },
                {
                  query: "email",
                  label: message({ id: "email" }),
                  type: "text",
                },
                {
                  query: "gender",
                  label: message({ id: "gender" }),
                  type: "gender",
                },
                {
                  query: "createdAt",
                  label: message({ id: "createdOn" }),
                  type: "datetime",
                },
                {
                  query: "birthDate",
                  label: message({ id: "dateOfBirth" }),
                  type: "date",
                },
              ]}
              setFilters={setSelectedFilters}
              total={membersInSegment?.total}
            />
            <Box display="flex" alignItems="center" gap={10}>
              <StatusValue
                status="neutral"
                label={
                  <>
                    <RefreshCcw04Icon />
                    {message({ id: "lastRecalculation" })}:{" "}
                    {!!lastRecalculation ? OLDate(lastRecalculation) : "-"}
                  </>
                }
              />
              {isStaticSegment ? (
                <SubheaderActionButton
                  data-test-id="member-in-segment"
                  disabled={hasViewImportPermission}
                  onClick={() =>
                    setSelectedModal({
                      mode: Modals.ConfirmImport,
                      isOpen: true,
                    })
                  }
                  text={message({
                    id: "replaceMembersByCsv",
                  }).toUpperCase()}
                />
              ) : null}
            </Box>
          </Subheader>
          <OLTable
            fullView
            setColumnOrdering={setColumnOrdering}
            tableName={MEMBERS_IN_SEGMENT_LIST_ID}
            data-test-id="member-in-segment"
            sort={{
              order: listing.order || "desc",
              property: listing.property || "createdAt",
              rowsPerPage: listing.rowsPerPage,
              page: listing.page,
            }}
            actions={{
              body: [
                {
                  action: (
                    id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
                  ) => navigate(links.singleMemberGeneralInfo(id as string)),
                  label: message({ id: "seeFullProfile" }),
                },
                {
                  action: (
                    id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
                  ) => navigate(links.editMember(id as string)),
                  label: message({ id: "editProfile" }),
                },
              ],
            }}
            columns={membersInSegment && headItems}
            rows={membersInSegment && items}
            onSort={(prop) =>
              updateListing({
                payload: prop,
                type: ListingActions.SET_SORT,
              })
            }
            pagination={{
              count: calculateCount(
                membersInSegment?.total.filtered,
                membersInSegment?.total.all,
              ),
              onChangePage: (page) => {
                updateListing({
                  type: ListingActions.CHANGE_PAGE,
                  payload: page,
                });
              },
              onChangeRowsPerPage: (rows) => {
                updateListing({
                  type: ListingActions.CHANGE_ROWS_PER_PAGE,
                  payload: rows,
                });
              },
              page: listing.page,
              rowsPerPage: listing.rowsPerPage,
            }}
          />
        </>
      )}
      {selectedModal.mode === Modals.ImportCsv && (
        <ImportCsvModal
          headerMessage="importMembers"
          identificationFactor
          closeModal={() => setSelectedModal({ mode: null, isOpen: false })}
          isModalOpen={selectedModal.isOpen}
          openImportStartedModal={() =>
            setSelectedModal({ mode: Modals.ImportStarted, isOpen: true })
          }
          importData={importSegmentMembers}
        />
      )}
      {selectedModal.mode === Modals.ImportStarted && (
        <ImportStartedModal
          header="importMembers"
          isOpen={selectedModal.isOpen}
          onClose={() => {
            setSelectedModal({ mode: null, isOpen: false });
            navigate(links.importsExports);
          }}
          action={() => {
            setSelectedModal({ mode: null, isOpen: false });
            navigate(links.segmentsList);
            notify(message({ id: "importHasStartedViewProgress" }), {
              button: {
                text: message({ id: "view" }),
                onClick: ({ closeNotification }) => {
                  closeNotification();
                  return navigate(links.importsExports, {
                    state: { activeTab: "imports" },
                  });
                },
              },
            });
          }}
        />
      )}
      {selectedModal.mode === Modals.ConfirmImport && (
        <ConfirmationModal
          open={selectedModal.isOpen}
          header="replaceMembersTitle"
          info={
            <Typography className={classes.subtitle}>
              {message({
                id: "replaceMembersInfo",
              })}
            </Typography>
          }
          submitText="continue"
          cancelText={message({ id: "cancel" })}
          close={() => {
            setSelectedModal({ mode: null, isOpen: false });
          }}
          action={() =>
            setSelectedModal({ mode: Modals.ImportCsv, isOpen: true })
          }
        />
      )}
    </>
  );
}
